(() => {
  const SECTION_SELECTOR = '.oddit-section-reviews-carousel';
  const SLIDE_CLASS = 'oddit-section-reviews-carousel__slide';
  const IMAGE_SLIDE_CLASS = 'oddit-section-reviews-carousel__image-slide';
  const DOT_ACTIVE_CLASS = 'oddit-section-reviews-carousel__dot--active';
  const IMAGE_ACTIVE_CLASS = 'oddit-section-reviews-carousel__image-slide--active';
  const SCROLL_SETTLE_MS = 600;
  const sectionState = new Map();

  function initSection(section) {
    initVideoModal(section);

    /* Slide content shouldn't depend on IntersectionObserver-driven reveals:
       IO is fragile inside a horizontally-scrolled overflow container (slides
       2..N start out of viewport horizontally), and the carousel also clones
       slides after IO has already wired itself up, leaving cloned elements
       observed by nothing. Mark every reveal element in the section visible
       up front — the carousel's scroll-snap motion is the entrance effect. */
    section.querySelectorAll('.oddit-reveal, .oddit-entrance').forEach((n) => n.classList.add('is-visible'));

    const reviewsTrack = section.querySelector('[data-role="reviews"]');
    const imagesTrack = section.querySelector('[data-role="images"]');
    const dots = section.querySelectorAll('.oddit-section-reviews-carousel__dot');
    if (!reviewsTrack) return;

    const realReviews = getChildrenByClass(reviewsTrack, SLIDE_CLASS);
    if (realReviews.length === 0) return;
    const realImages = imagesTrack ? getChildrenByClass(imagesTrack, IMAGE_SLIDE_CLASS) : [];

    const totalReal = realReviews.length;

    /* Clone ALL real slides on both sides so peeks are never empty */
    cloneAllEnds(reviewsTrack, realReviews);
    if (imagesTrack && realImages.length > 0) cloneAllEnds(imagesTrack, realImages);

    const allReviews = getChildrenByClass(reviewsTrack, SLIDE_CLASS);
    const allImages = imagesTrack ? getChildrenByClass(imagesTrack, IMAGE_SLIDE_CLASS) : [];
    const totalVirtual = allReviews.length;

    /* Middle copy is the "real" range: [totalReal, totalReal*2 - 1] */
    const state = {
      virtualIndex: totalReal,
      realIndex: 0,
      totalReal,
      totalVirtual,
      autoplay: null,
      isSyncing: false,
      isJumping: false,
      userInteracted: false,
      isSyncingTimer: null,
      cloneSnapTimer: null
    };

    requestAnimationFrame(() => {
      setScrollPos(reviewsTrack, allReviews, totalReal, false);
      if (imagesTrack) setScrollPos(imagesTrack, allImages, totalReal, false);
      updateActiveImage(allImages, totalReal);
      updateDots(dots, 0);
    });

    function goToVirtual(virtualIdx, smooth) {
      state.virtualIndex = virtualIdx;
      state.realIndex = ((virtualIdx % totalReal) + totalReal) % totalReal;
      setSyncing(state, smooth ? SCROLL_SETTLE_MS : 60);
      setScrollPos(reviewsTrack, allReviews, virtualIdx, smooth);
      if (imagesTrack) setScrollPos(imagesTrack, allImages, virtualIdx, smooth);
      updateActiveImage(allImages, virtualIdx);
      updateDots(dots, state.realIndex);

      if (smooth && isInCloneRange(virtualIdx, totalReal)) {
        scheduleCloneSnap(virtualIdx);
      }
    }

    function scheduleCloneSnap(fromVirtual) {
      if (state.cloneSnapTimer) clearTimeout(state.cloneSnapTimer);
      state.cloneSnapTimer = setTimeout(() => {
        state.cloneSnapTimer = null;
        if (state.virtualIndex !== fromVirtual) return;
        performCloneSnap(fromVirtual);
      }, SCROLL_SETTLE_MS);
    }

    function performCloneSnap(fromVirtual) {
      const snapTo = totalReal + ((fromVirtual % totalReal) + totalReal) % totalReal;
      state.isJumping = true;
      state.virtualIndex = snapTo;
      state.realIndex = ((snapTo % totalReal) + totalReal) % totalReal;
      setScrollPos(reviewsTrack, allReviews, snapTo, false);
      if (imagesTrack) setScrollPos(imagesTrack, allImages, snapTo, false);
      updateActiveImage(allImages, snapTo);
      setTimeout(() => { state.isJumping = false; }, 60);
    }

    state.advance = () => goToVirtual(state.virtualIndex + 1, true);
    state.retreat = () => goToVirtual(state.virtualIndex - 1, true);
    state.goToReal = (realIdx) => goToVirtual(totalReal + realIdx, true);

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.getAttribute('data-slide-index'), 10);
        state.goToReal(idx);
        stopAutoplayPermanently(state);
      });
    });

    const arrows = section.querySelectorAll('[data-arrow-direction]');
    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const direction = arrow.getAttribute('data-arrow-direction');
        if (direction === 'next') state.advance();
        else if (direction === 'prev') state.retreat();
        stopAutoplayPermanently(state);
      });
    });

    attachScrollSync(reviewsTrack, allReviews, state, (detected) => {
      syncOther(imagesTrack, allImages, detected);
      updateDots(dots, state.realIndex);
      updateActiveImage(allImages, detected);
      if (isInCloneRange(detected, totalReal)) scheduleCloneSnap(detected);
      stopAutoplayPermanently(state);
    });

    if (imagesTrack) {
      attachScrollSync(imagesTrack, allImages, state, (detected) => {
        syncOther(reviewsTrack, allReviews, detected);
        updateDots(dots, state.realIndex);
        updateActiveImage(allImages, detected);
        if (isInCloneRange(detected, totalReal)) scheduleCloneSnap(detected);
        stopAutoplayPermanently(state);
      });
    }

    state.autoplay = initAutoplay(section, state);
    sectionState.set(section.id || section, state);
  }

  function isInCloneRange(virtualIdx, totalReal) {
    return virtualIdx < totalReal || virtualIdx >= totalReal * 2;
  }

  function getChildrenByClass(parent, cls) {
    return Array.from(parent.children).filter((el) => el.classList.contains(cls));
  }

  function cloneAllEnds(track, realSlides) {
    /* Prepend reversed so left clones end up in original order */
    realSlides.slice().reverse().forEach((slide) => {
      const clone = slide.cloneNode(true);
      markAsClone(clone);
      track.insertBefore(clone, track.firstChild);
    });
    realSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      markAsClone(clone);
      track.appendChild(clone);
    });
  }

  function markAsClone(el) {
    el.setAttribute('data-clone', 'true');
    el.setAttribute('aria-hidden', 'true');
    el.querySelectorAll('button, a, [tabindex]').forEach((n) => n.setAttribute('tabindex', '-1'));
    /* Clones are added after oddit-interactions.js has already observed the
       originals, so the cloned .oddit-reveal / .oddit-entrance elements would
       never receive .is-visible and would stay opacity:0. Mark them visible
       up-front since clones are functionally identical to the originals. */
    if (el.classList.contains('oddit-reveal') || el.classList.contains('oddit-entrance')) {
      el.classList.add('is-visible');
    }
    el.querySelectorAll('.oddit-reveal, .oddit-entrance').forEach((n) => n.classList.add('is-visible'));
  }

  function setScrollPos(track, slides, virtualIdx, smooth) {
    if (!track || !slides[virtualIdx]) return;
    const slide = slides[virtualIdx];
    const offset = slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
    track.scrollTo({ left: offset, behavior: smooth ? 'smooth' : 'instant' });
  }

  function detectCenterSlide(track, slides) {
    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let closestIdx = 0;
    let closestDist = Infinity;
    slides.forEach((slide, i) => {
      const center = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(center - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = i;
      }
    });
    return closestIdx;
  }

  function syncOther(track, slides, virtualIdx) {
    if (!track || !slides || slides.length === 0) return;
    setScrollPos(track, slides, virtualIdx, true);
  }

  function attachScrollSync(track, slides, state, onChange) {
    let pending = false;
    track.addEventListener('scroll', () => {
      if (state.isSyncing || state.isJumping) return;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        const detected = detectCenterSlide(track, slides);
        if (detected !== state.virtualIndex) {
          state.virtualIndex = detected;
          state.realIndex = ((detected % state.totalReal) + state.totalReal) % state.totalReal;
          setSyncing(state, SCROLL_SETTLE_MS);
          onChange(detected);
        }
        pending = false;
      });
    }, { passive: true });
  }

  function setSyncing(state, ms) {
    state.isSyncing = true;
    if (state.isSyncingTimer) clearTimeout(state.isSyncingTimer);
    state.isSyncingTimer = setTimeout(() => {
      state.isSyncing = false;
      state.isSyncingTimer = null;
    }, ms);
  }

  function updateDots(dots, activeRealIndex) {
    dots.forEach((dot, i) => {
      dot.classList.toggle(DOT_ACTIVE_CLASS, i === activeRealIndex);
    });
  }

  function updateActiveImage(imageSlides, activeVirtualIndex) {
    if (!imageSlides) return;
    imageSlides.forEach((slide, i) => {
      slide.classList.toggle(IMAGE_ACTIVE_CLASS, i === activeVirtualIndex);
    });
  }

  function stopAutoplayPermanently(state) {
    if (state.userInteracted) return;
    state.userInteracted = true;
    if (state.autoplay) {
      state.autoplay.destroy();
      state.autoplay = null;
    }
  }

  function initAutoplay(section, state) {
    const autoplayEnabled = section.dataset.autoplay === 'true';
    if (!autoplayEnabled) return null;

    const speedSeconds = parseInt(section.dataset.autoplaySpeed, 10) || 5;
    const intervalMs = speedSeconds * 1000;

    let rafId = null;
    let lastAdvance = performance.now();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      return { start() {}, stop() {}, destroy() {} };
    }

    function tick(now) {
      if (now - lastAdvance >= intervalMs) {
        if (state.advance) state.advance();
        lastAdvance = now;
      }
      rafId = requestAnimationFrame(tick);
    }

    function start() {
      if (rafId) return;
      lastAdvance = performance.now();
      rafId = requestAnimationFrame(tick);
    }

    function stop() {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    function destroy() {
      stop();
    }

    document.addEventListener('visibilitychange', () => {
      if (state.userInteracted) return;
      document.hidden ? stop() : start();
    });

    reducedMotion.addEventListener('change', (e) => {
      if (state.userInteracted) return;
      e.matches ? stop() : start();
    });

    start();

    return { start, stop, destroy };
  }

  function destroySection(section) {
    const key = section.id || section;
    const state = sectionState.get(key);
    if (!state) return;
    if (state.autoplay) state.autoplay.destroy();
    sectionState.delete(key);
  }

  /* ---------- Video modal ---------- */
  function initVideoModal(section) {
    const modal = section.querySelector('[data-video-modal]');
    const videoContainer = section.querySelector('[data-modal-video]');
    if (!modal || !videoContainer) return;

    section.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-play-trigger]');
      if (!trigger || !section.contains(trigger)) return;
      e.preventDefault();
      openVideoModal(modal, videoContainer, {
        url: trigger.dataset.playUrl || '',
        src: trigger.dataset.playSrc || ''
      });
    });

    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-modal-close]')) {
        closeVideoModal(modal, videoContainer);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) {
        closeVideoModal(modal, videoContainer);
      }
    });
  }

  function openVideoModal(modal, videoContainer, { url, src }) {
    let html = '';
    if (url) {
      const parsed = parseVideoUrl(url);
      if (parsed && parsed.type === 'youtube') {
        html = `<iframe src="https://www.youtube.com/embed/${parsed.id}?autoplay=1&rel=0&playsinline=1" title="Video player" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
      } else if (parsed && parsed.type === 'vimeo') {
        html = `<iframe src="https://player.vimeo.com/video/${parsed.id}?autoplay=1&playsinline=1" title="Video player" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
      }
    }
    if (!html && src) {
      html = `<video src="${src}" autoplay controls playsinline></video>`;
    }
    if (!html) return;

    videoContainer.innerHTML = html;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeVideoModal(modal, videoContainer) {
    videoContainer.innerHTML = '';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  function parseVideoUrl(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (yt) return { type: 'youtube', id: yt[1] };
    const vm = url.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
    if (vm) return { type: 'vimeo', id: vm[1] };
    return null;
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
